import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import CategoryGrid from "../components/CategoryGrid";
import ListingCard from "../components/ListingCard";
import EventCard from "../components/EventCard";
import StatsSection from "../components/StatsSection";
import Testimonials from "../components/Testimonials";
import FAQ from "../components/FAQ";
import { CTA, PartnersMarquee } from "../components/CTA";
import { publicApi } from "../lib/api";
import { SITE_CONFIG } from "../config/site";
import { useContent, renderHighlightedTitle } from "../lib/useContent";
import { PageSEO } from "../components/PageSEO";
import { ArrowRight } from "lucide-react";

// =============================================================================
// HOME PAGE
// =============================================================================
// The Home is the entry point of the portal. Its most important role is to
// give EQUAL visibility to all associates — no card gets special treatment.
//
// The "Le attività del centro" section calls /api/businesses/spotlight which
// returns a random subset of PUBLISHED businesses. To avoid immediate repeats
// within the same session, we remember the last shown IDs in sessionStorage
// and pass them as `?exclude=` on subsequent loads.
//
// DO NOT introduce any "featured" flag, "premium" tier or category boost here:
// the parity principle is a hard requirement of the CCN design brief.
// =============================================================================

// SessionStorage key + helpers: track recently shown business IDs so a user
// reloading the Home doesn't see the same 6 cards on every visit.
const SESSION_SHOWN_KEY = "ccn_home_shown";
const readShown = () => { try { return JSON.parse(sessionStorage.getItem(SESSION_SHOWN_KEY) || "[]"); } catch { return []; } };
const writeShown = (ids) => { try { sessionStorage.setItem(SESSION_SHOWN_KEY, JSON.stringify(ids)); } catch { /* noop */ } };

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const { field, seo } = useContent("home");

  // Home content blocks (CMS-managed)
  const spotlightPretitle = field("spotlight", "pretitle", "Le attività del centro");
  const spotlightHeadline = field("spotlight", "headline", "Scopri le realtà del centro.");
  const spotlightHighlight = field("spotlight", "highlight_terms", ["del centro"]);
  const spotlightSubtitle = field("spotlight", "subtitle", "Una selezione a rotazione dei nostri associati.");
  const spotlightCtaLabel = field("spotlight", "cta_label", "Vedi tutte le attività");
  const spotlightCtaUrl = field("spotlight", "cta_url", "/attivita");

  const joinPretitle = field("join_banner", "pretitle", "Fai crescere la tua attività");
  const joinHeadline = field("join_banner", "headline", "Unisciti al CCN.");
  const joinSubheadline = field("join_banner", "subheadline", "La tua vetrina inizia qui.");
  const joinDescription = field("join_banner", "description", "Diventa socio del Centro Commerciale Naturale di Sanluri. Scheda pubblica ricca, eventi condivisi, promozioni congiunte e una comunità viva di imprese del centro.");
  const joinCtaLabel = field("join_banner", "cta_label", "Associati ora");
  const joinCtaUrl = field("join_banner", "cta_url", "/diventa-socio");
  const joinMicroText = field("join_banner", "micro_text", "Compilazione in 5 minuti · Risposta entro pochi giorni");

  const evPretitle = field("events_section", "pretitle", "Cosa succede a Sanluri");
  const evHeadline = field("events_section", "headline", "Scopri gli eventi del nostro paese.");
  const evHighlight = field("events_section", "highlight_terms", ["eventi"]);
  const evCtaLabel = field("events_section", "cta_label", "Tutti gli eventi");
  const evCtaUrl = field("events_section", "cta_url", "/eventi");

  // Random selection is fetched server-side (see /api/businesses/spotlight).
  // Memory is capped at SITE_CONFIG.HOME.SESSION_MEMORY IDs to prevent the
  // exclude list from growing indefinitely.
  useEffect(() => {
    const prev = readShown();
    publicApi.spotlight(SITE_CONFIG.HOME.SPOTLIGHT_LIMIT, prev)
      .then((r) => {
        const rows = r.data || [];
        setFeatured(rows);
        const merged = [...rows.map((x) => x.id), ...prev].slice(0, SITE_CONFIG.HOME.SESSION_MEMORY);
        writeShown([...new Set(merged)]);
      })
      .catch(() => setFeatured([]))
      .finally(() => setLoading(false));
  }, []);

  // Events — SINGLE SOURCE OF TRUTH: /api/events (identical ordering to /eventi page).
  // Only published, non-deleted, visible, ongoing+upcoming events are surfaced.
  // Ongoing (started but not ended) appear FIRST with a live "In corso" badge.
  useEffect(() => {
    setEventsLoading(true);
    publicApi.listEvents({ upcoming: true, limit: 3 })
      .then((r) => setUpcomingEvents(r.data || []))
      .catch(() => setUpcomingEvents([]))
      .finally(() => setEventsLoading(false));
  }, []);

  // Tick minutario: forza re-render dell'EventCard per aggiornare live il badge
  // "In corso" quando end_at scade, senza refresh manuale.
  const [, setNowMs] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div>
      <PageSEO
        seo={seo}
        defaults={{
          title: "CCN Sanluri — Il centro commerciale naturale di Sanluri",
          description: "Attività, eventi e vita del centro di Sanluri: scopri negozi, artigiani, bar e servizi del CCN.",
        }}
      />
      <Hero />
      <CategoryGrid />

      {featured.length > 0 && (
        <section className="py-24 lg:py-32 bg-[#F8F9FA]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div>
                {spotlightPretitle !== "" && <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-4">{spotlightPretitle}</div>}
                <h2 className="font-display font-bold text-4xl md:text-6xl leading-[1.02] tracking-tight text-[#1F2937] max-w-3xl">
                  {renderHighlightedTitle(spotlightHeadline, spotlightHighlight)}
                </h2>
                {spotlightSubtitle !== "" && <p className="text-slate-500 text-sm mt-3">{spotlightSubtitle}</p>}
              </div>
              {spotlightCtaLabel !== "" && (
                <Link to={spotlightCtaUrl} className="group inline-flex items-center gap-2 text-[#1F2937] text-lg link-underline">
                  {spotlightCtaLabel}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((l) => <ListingCard key={l.id} listing={l} />)}
            </div>
          </div>
        </section>
      )}

      {loading && featured.length === 0 && (
        <section className="py-24 lg:py-32 bg-[#F8F9FA]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({length:3}).map((_,i)=>(
                <div key={`sk-listing-${i}`} className="bg-white rounded-3xl overflow-hidden border border-[#F1F5F9]">
                  <div className="aspect-[16/10] skeleton" />
                  <div className="p-6 space-y-3"><div className="h-4 skeleton rounded w-24" /><div className="h-6 skeleton rounded w-3/4" /><div className="h-4 skeleton rounded w-full" /></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <StatsSection />

      {/* Banner Diventa socio */}
      <section className="py-24 lg:py-28 bg-gradient-to-br from-[#12964A] via-[#12964A] to-[#0E7C3D] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #F4E500 0px, transparent 50%), radial-gradient(circle at 80% 70%, #6FC247 0px, transparent 40%)" }} />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8 text-white">
              {joinPretitle !== "" && <div className="text-xs uppercase tracking-[0.22em] text-[#F4E500] mb-4">{joinPretitle}</div>}
              <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                {joinHeadline}<br />
                {joinSubheadline !== "" && <span className="text-[#F4E500]">{joinSubheadline}</span>}
              </h2>
              {joinDescription !== "" && <p className="text-lg text-white/85 mt-6 max-w-2xl">{joinDescription}</p>}
            </div>
            <div className="lg:col-span-4 lg:text-right">
              {joinCtaLabel !== "" && (
                <Link to={joinCtaUrl} className="inline-flex items-center gap-3 bg-[#F4E500] hover:bg-[#e5d700] text-[#1F2937] font-medium rounded-full px-8 py-4 text-base transition-colors shadow-xl" data-testid="home-join-cta">
                  {joinCtaLabel}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              )}
              {joinMicroText !== "" && <div className="text-xs text-white/60 mt-3">{joinMicroText}</div>}
            </div>
          </div>
        </div>
      </section>

      {/* Events — rendered ONLY if the API returned at least one published event.
          Zero fallback, zero mock, zero dead links: coherent with /eventi. */}
      {!eventsLoading && upcomingEvents.length > 0 && (
        <section className="py-24 lg:py-32">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div>
                {evPretitle !== "" && (
                  <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-4 inline-flex items-center gap-2">
                    {evPretitle}
                  </div>
                )}
                <h2 className="font-display font-bold text-4xl md:text-6xl leading-[1.02] tracking-tight text-[#1F2937] max-w-3xl">
                  {renderHighlightedTitle(evHeadline, evHighlight)}
                </h2>
              </div>
              {evCtaLabel !== "" && (
                <Link to={evCtaUrl} data-testid="home-all-events-link" className="group inline-flex items-center gap-2 text-[#1F2937] text-lg link-underline">
                  {evCtaLabel}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-events-grid">
              {upcomingEvents.map((e) => <EventCard key={e.id} event={e} />)}
            </div>
          </div>
        </section>
      )}

      <Testimonials />
      <FAQ />
      <PartnersMarquee />
      <CTA />
    </div>
  );
};

export default Home;
